import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { X } from 'lucide-react';
import { useEvent, useApiErrors } from '@soap/modules';
import { tenantCustomCatalogSubjectRowsFromLibrary } from '../utils/curriculumUtils';
import { CustomSubjectForm, useCustomSubjectFormState } from './CustomSubjectForm';
import { AppMessageDialog } from '../../AppMessageDialog';

const wizardSteps = {
  subjectSelect: 'subjectSelect',
  createSubject: 'createSubject',
  topicMode: 'topicMode'
} as const;

type WizardStep = (typeof wizardSteps)[keyof typeof wizardSteps];

export interface CreateYourOwnWizardDialogProps {
  showDialog: boolean;
  onClose: () => void;
  tenantCustomCurriculumLibraryData?: any | null;
  onLoadEducationSystems?: () => void;
  onEnsureSubject: (command: { c316_SubjectName: string; c316_EducationSystemKey: string }) => void;
  onRefreshTenantCustomCurriculum?: () => void;
  onOpenSingleTopicDialog: (catalogSubjectId: string) => void;
  onOpenBatchTopicsDialog: (catalogSubjectId: string) => void;
  /** Parent sets true while wizard is open so E181 (C316) does not close the wizard. */
  onWizardActiveChange?: (active: boolean) => void;
}

export function CreateYourOwnWizardDialog({
  showDialog,
  onClose,
  tenantCustomCurriculumLibraryData,
  onLoadEducationSystems,
  onEnsureSubject,
  onRefreshTenantCustomCurriculum,
  onOpenSingleTopicDialog,
  onOpenBatchTopicsDialog,
  onWizardActiveChange
}: CreateYourOwnWizardDialogProps) {
  const navigate = useNavigate();
  const catalogTitle = 'Custom curriculum (catalog)';
  const [step, setStep] = useState<WizardStep>(wizardSteps.subjectSelect);
  const [selectedCatalogSubjectId, setSelectedCatalogSubjectId] = useState('');
  const [awaitingC316, setAwaitingC316] = useState(false);
  const [appMessage, setAppMessage] = useState<{ title: string; message: string } | null>(null);
  const subjectForm = useCustomSubjectFormState(step === wizardSteps.createSubject, onLoadEducationSystems);

  useApiErrors(() => {
    setAwaitingC316(false);
  });

  const subjectRows = useMemo(
    () => tenantCustomCatalogSubjectRowsFromLibrary(tenantCustomCurriculumLibraryData),
    [tenantCustomCurriculumLibraryData]
  );

  const catalogReady = tenantCustomCurriculumLibraryData != null;
  const hasSubjects = subjectRows.length > 0;
  const subjectSelected = selectedCatalogSubjectId.trim().length > 0;

  useEffect(() => {
    onWizardActiveChange?.(showDialog);
  }, [showDialog, onWizardActiveChange]);

  useEffect(() => {
    if (!showDialog) {
      setStep(wizardSteps.subjectSelect);
      setSelectedCatalogSubjectId('');
      setAwaitingC316(false);
      subjectForm.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when dialog closes
  }, [showDialog]);

  useEffect(() => {
    if (!showDialog) return;
    onRefreshTenantCustomCurriculum?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open-only refresh
  }, [showDialog]);

  useEffect(() => {
    if (!selectedCatalogSubjectId) return;
    if (!subjectRows.some((r) => r.catalogSubjectId === selectedCatalogSubjectId)) {
      setSelectedCatalogSubjectId('');
    }
  }, [subjectRows, selectedCatalogSubjectId]);

  useEvent({
    eventName: 'E181v1_TopicsAddedToStudentCurriculum',
    onEventReceived: (event: any) => {
      if (!awaitingC316) return;
      const tenantCustomSubjectId =
        event?.e181_TenantCustomSubjectId ?? event?.E181_TenantCustomSubjectId;
      const myId = event?.e181_StudentCurriculumId ?? event?.E181_StudentCurriculumId;
      const studentCurriculumAdd =
        myId && String(myId).replace(/[{}]/g, '') !== '00000000-0000-0000-0000-000000000000';
      const tenantCustomOnly = Boolean(tenantCustomSubjectId) && !studentCurriculumAdd;
      if (!tenantCustomOnly || !tenantCustomSubjectId) return;
      setSelectedCatalogSubjectId(String(tenantCustomSubjectId).trim());
      setAwaitingC316(false);
      setStep(wizardSteps.subjectSelect);
      subjectForm.reset();
    }
  });

  if (!showDialog) return null;

  const handleCancel = () => {
    if (awaitingC316) return;
    onClose();
  };

  const handleSaveSubject = () => {
    if (!subjectForm.canSave || awaitingC316) return;
    const cmd = subjectForm.getEnsureSubjectCommand();
    const nameKey = cmd.c316_SubjectName.trim().toLowerCase();
    const systemKey = cmd.c316_EducationSystemKey.trim();
    const duplicate = subjectRows.some(
      (r) =>
        r.name.trim().toLowerCase() === nameKey &&
        r.educationSystemKey.trim() === systemKey
    );
    if (duplicate) {
      setAppMessage({
        title: 'Subject already exists',
        message:
          'A custom subject with this name and education system is already in your catalog. Choose it from the list on the previous step, or use a different name or education system.'
      });
      return;
    }
    setAwaitingC316(true);
    onEnsureSubject(cmd);
  };

  const handleNextFromSubject = () => {
    if (!subjectSelected) return;
    setStep(wizardSteps.topicMode);
  };

  const handleBackFromCreateSubject = () => {
    if (awaitingC316) return;
    subjectForm.reset();
    setStep(wizardSteps.subjectSelect);
  };

  const handleBackFromTopicMode = () => {
    setStep(wizardSteps.subjectSelect);
  };

  const handleOpenSingle = () => {
    const id = selectedCatalogSubjectId.trim();
    if (!id) return;
    onClose();
    onOpenSingleTopicDialog(id);
  };

  const handleOpenBatch = () => {
    const id = selectedCatalogSubjectId.trim();
    if (!id) return;
    onClose();
    onOpenBatchTopicsDialog(id);
  };

  const title =
    step === wizardSteps.createSubject
      ? `Create subject — ${catalogTitle}`
      : step === wizardSteps.topicMode
        ? `Add topics — ${catalogTitle}`
        : 'Create Your Own Custom Curriculum';

  return (
    <>
      <AppMessageDialog
        open={appMessage != null}
        onOpenChange={(open) => {
          if (!open) setAppMessage(null);
        }}
        title={appMessage?.title ?? ''}
        message={appMessage?.message ?? ''}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="mx-4 flex w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={handleCancel}
            disabled={awaitingC316}
            className="text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {step === wizardSteps.subjectSelect && (
            <div className="space-y-4">
              {!catalogReady && <p className="text-sm text-gray-600">Loading catalog…</p>}
              {catalogReady && hasSubjects && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Subject <span className="text-destructive-action">*</span>
                  </label>
                  <select
                    value={selectedCatalogSubjectId}
                    onChange={(e) => setSelectedCatalogSubjectId(e.target.value)}
                    disabled={awaitingC316}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue disabled:bg-gray-100"
                  >
                    <option value="">Select a subject</option>
                    {subjectRows.map((r) => (
                      <option key={r.catalogSubjectId} value={r.catalogSubjectId}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-600">
                    Only custom subjects you created appear here.
                  </p>
                </div>
              )}
              {catalogReady && !hasSubjects && (
                <p className="text-sm text-gray-700">
                  You do not have any custom subjects yet. Create one to continue.
                </p>
              )}
              {catalogReady && !subjectSelected && (
                <button
                  type="button"
                  onClick={() => setStep(wizardSteps.createSubject)}
                  disabled={awaitingC316}
                  className="w-full rounded-lg border border-action-blue/30 bg-action-blue/10 px-4 py-3 text-sm font-medium text-action-blue-dark transition-colors hover:bg-action-blue/15 disabled:opacity-50"
                >
                  Add A Custom Subject
                </button>
              )}
              {awaitingC316 && (
                <p className="text-sm text-gray-600">Saving subject…</p>
              )}
            </div>
          )}

          {step === wizardSteps.createSubject && (
            <CustomSubjectForm
              active={step === wizardSteps.createSubject}
              onLoadEducationSystems={onLoadEducationSystems}
              onNavigateToEducationSystem={() => {
                onClose();
                navigate('/app/settings/education-system');
              }}
              subjectName={subjectForm.subjectName}
              setSubjectName={subjectForm.setSubjectName}
              educationSystems={subjectForm.educationSystems}
              educationSystemKey={subjectForm.educationSystemKey}
              setEducationSystemKey={subjectForm.setEducationSystemKey}
            />
          )}

          {step === wizardSteps.topicMode && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                How would you like to add topics to{' '}
                <span className="font-medium text-gray-900">
                  {subjectRows.find((r) => r.catalogSubjectId === selectedCatalogSubjectId)?.name ?? 'this subject'}
                </span>
                ?
              </p>
              <button
                type="button"
                onClick={handleOpenSingle}
                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-4 text-left transition-colors hover:border-action-blue/40 hover:bg-action-blue/10"
              >
                <span className="block text-sm font-semibold text-gray-900">Single custom topic</span>
                <span className="mt-1 block text-xs text-gray-600">
                  Add one topic with full details (hours, learning objectives, and more).
                </span>
              </button>
              <button
                type="button"
                onClick={handleOpenBatch}
                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-4 text-left transition-colors hover:border-action-blue/40 hover:bg-action-blue/10"
              >
                <span className="block text-sm font-semibold text-gray-900">Multiple topics with AI*</span>
                <span className="mt-1 block text-xs text-gray-600">
                  Generate a list of topic names, edit them, then create topics in batches.
                </span>
              </button>
              <p className="text-xs text-gray-500">* AI-generated names and content; review before saving.</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 p-6">
          <button
            type="button"
            onClick={handleCancel}
            disabled={awaitingC316}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          {step === wizardSteps.createSubject && (
            <>
              <button
                type="button"
                onClick={handleBackFromCreateSubject}
                disabled={awaitingC316}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSaveSubject}
                disabled={!subjectForm.canSave || awaitingC316}
                className="rounded-lg bg-action-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-action-blue-dark disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                Save
              </button>
            </>
          )}
          {step === wizardSteps.subjectSelect && subjectSelected && (
            <button
              type="button"
              onClick={handleNextFromSubject}
              disabled={awaitingC316}
              className="rounded-lg bg-action-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-action-blue-dark disabled:opacity-50"
            >
              Next
            </button>
          )}
          {step === wizardSteps.topicMode && (
            <button
              type="button"
              onClick={handleBackFromTopicMode}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
            >
              Back
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
